import React from 'react'
import {FormikProps} from 'formik'
import {IntlShape} from 'react-intl'
import {toast} from 'react-toastify'
import {TourDetails, Translation} from '../core/_models'
import {getOneByIdbylang, patchOne} from '../../../../services/common_requests'
import {
  buildTourTranslationPayload,
  buildTourTranslationSeoPayload,
  findTranslationByLanguage,
} from '../core/translationSubmit'
import { sanitizeTourPatchPayload } from '../core/tourPatchPayload'
import { mergeUniqueTags, parseTagInput } from '../core/tagInputHelpers'

interface SeoTabProps {
  formik: FormikProps<TourDetails>
  intl: IntlShape
  selectedLanguage: string
  tourId?: string
  allTranslations?: Translation[]
  onSaved?: () => void | Promise<void>
}

const SeoTab: React.FC<SeoTabProps> = ({
  formik,
  intl,
  selectedLanguage,
  tourId,
  allTranslations,
  onSaved,
}) => {
  const [keywordInput, setKeywordInput] = React.useState('')
  const [isSaving, setIsSaving] = React.useState(false)

  const getCurrentTranslation = () => {
    const translations = formik.values.translations || []
    let currentTranslation = findTranslationByLanguage(
      translations,
      selectedLanguage,
    ) as Translation | undefined

    if (!currentTranslation) {
      currentTranslation = {
        language: selectedLanguage,
        title: '',
        description: '',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: [],
        seoSlug: '',
        openGraphTitle: '',
        openGraphDescription: '',
        openGraphImage: '',
        twitterTitle: '',
        twitterDescription: '',
        twitterImage: '',
        openGraphType: 'website',
        openGraphLocale: selectedLanguage,
        twitterCardType: 'summary_large_image',
        metaRobots: 'index,follow',
        canonicalUrl: '',
        structuredData: '',
      } as Translation
      translations.push(currentTranslation)
    }

    const fromAll = findTranslationByLanguage(allTranslations, selectedLanguage) as
      | Translation
      | undefined
    if (fromAll) {
      for (const field of [
        'seoSlug',
        'metaTitle',
        'metaDescription',
        'metaKeywords',
        'openGraphTitle',
        'openGraphDescription',
        'openGraphImage',
        'openGraphType',
        'openGraphLocale',
        'twitterTitle',
        'twitterDescription',
        'twitterImage',
        'twitterCardType',
        'metaRobots',
        'canonicalUrl',
        'structuredData',
      ] as const) {
        const currentValue = (currentTranslation as any)[field]
        const sourceValue = (fromAll as any)[field]
        const isEmpty =
          currentValue == null ||
          currentValue === '' ||
          (Array.isArray(currentValue) && currentValue.length === 0)
        if (isEmpty && sourceValue != null && sourceValue !== '') {
          ;(currentTranslation as any)[field] = sourceValue
        }
      }
    }

    return {translations, currentTranslation}
  }

  const updateTranslation = (field: keyof Translation, value: any) => {
    const {translations, currentTranslation} = getCurrentTranslation()
    if (currentTranslation) {
      ;(currentTranslation as any)[field] = value
      formik.setFieldValue('translations', translations)
    }
  }

  const addKeywords = (rawInput: string): string[] | null => {
    const parsed = parseTagInput(rawInput)
    if (!parsed.length) return null

    const {translations, currentTranslation} = getCurrentTranslation()
    if (!currentTranslation) return null

    const merged = mergeUniqueTags(currentTranslation.metaKeywords || [], parsed)
    currentTranslation.metaKeywords = merged
    formik.setFieldValue('translations', translations)
    setKeywordInput('')
    return merged
  }

  const handleAddKeyword = () => {
    addKeywords(keywordInput)
  }

  const handleRemoveKeyword = (index: number) => {
    const {translations, currentTranslation} = getCurrentTranslation()
    if (currentTranslation?.metaKeywords) {
      currentTranslation.metaKeywords.splice(index, 1)
      formik.setFieldValue('translations', translations)
    }
  }

  const resolveTranslationId = async (): Promise<string | undefined> => {
    const knownId = findTranslationByLanguage(allTranslations, selectedLanguage)?._id
    if (knownId) return String(knownId)

    if (!tourId) return undefined

    const refreshed = await getOneByIdbylang(`/tours/${tourId}?language=all`)
    return findTranslationByLanguage(refreshed?.translations, selectedLanguage)?._id
  }

  const handleSave = async () => {
    const pendingKeywords = keywordInput.trim() ? addKeywords(keywordInput) : null

    if (!tourId) {
      await formik.submitForm()
      return
    }

    setIsSaving(true)
    try {
      const {currentTranslation} = getCurrentTranslation()
      if (!currentTranslation) return

      if (pendingKeywords) {
        currentTranslation.metaKeywords = pendingKeywords
      }

      const seoSlug = (currentTranslation.seoSlug || '').trim()
      if (seoSlug) {
        currentTranslation.seoSlug = seoSlug
        if (selectedLanguage === 'fr' || !formik.values.seoSlug) {
          await patchOne(sanitizeTourPatchPayload({seoSlug}), `/tours/${tourId}`)
        }
      }

      let translationId = await resolveTranslationId()

      if (!translationId) {
        await patchOne(
          sanitizeTourPatchPayload({
            translations: [
              buildTourTranslationPayload(
                formik.values,
                selectedLanguage,
                allTranslations,
              ),
            ],
          }),
          `/tours/${tourId}`,
        )
        translationId = await resolveTranslationId()
      }

      if (!translationId) {
        throw new Error('Unable to resolve tour translation for SEO save')
      }

      await patchOne(
        buildTourTranslationSeoPayload(
          currentTranslation as Record<string, unknown>,
          selectedLanguage,
        ),
        `/tours/${tourId}/translations/${translationId}/seo`,
      )

      toast.success(
        intl.formatMessage(
          {id: 'TOUR.UPDATED_SUCCESSFULLY'},
          {defaultMessage: 'Tour updated successfully'},
        ),
      )
      await onSaved?.()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to save SEO data',
      )
    } finally {
      setIsSaving(false)
    }
  }

  const {currentTranslation} = getCurrentTranslation()

  if (!currentTranslation) {
    return null
  }

  return (
    <div className='d-flex flex-column gap-7 gap-lg-10'>
      <div className='card card-flush py-4'>
        <div className='card-header'>
          <div className='card-title'>
            <h2>{intl.formatMessage({id: 'TOUR.FIELDS.SEO'})}</h2>
          </div>
        </div>
        <div className='card-body pt-0'>
          <div className='row mb-8'>
            <div className='col-md-6'>
              <label className='form-label fw-semibold'>
                {intl.formatMessage({
                  id: 'COLUMNS.SEO_SLUG',
                  defaultMessage: 'URL slug (SEO)',
                })}
              </label>
              <input
                type='text'
                className='form-control'
                name='seoSlug'
                value={currentTranslation.seoSlug || ''}
                onChange={(e) => updateTranslation('seoSlug', e.target.value)}
                onBlur={formik.handleBlur}
                placeholder={
                  formik.values.seoSlug ||
                  (selectedLanguage === 'en'
                    ? 'tunisia-desert-tour'
                    : selectedLanguage === 'de'
                      ? 'tunesien-wueste-reise'
                      : 'circuit-desert-tunisie')
                }
              />
              <div className='form-text text-muted'>
                {intl.formatMessage(
                  {
                    id: 'TOUR.SEO.SLUG_PER_LANGUAGE',
                    defaultMessage: 'URL slug for {language} (e.g. /{language}/tour/your-slug)',
                  },
                  {language: selectedLanguage},
                )}
              </div>
            </div>
          </div>

          <div className='mb-8'>
            <h3 className='mb-4'>Meta Tags</h3>
            <div className='row mb-4'>
              <div className='col-md-6'>
                <label className='form-label'>Meta Title</label>
                <input
                  type='text'
                  className='form-control'
                  value={currentTranslation.metaTitle || ''}
                  onChange={(e) => updateTranslation('metaTitle', e.target.value)}
                  placeholder='Meta Title'
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Meta Description</label>
                <textarea
                  className='form-control'
                  value={currentTranslation.metaDescription || ''}
                  onChange={(e) => updateTranslation('metaDescription', e.target.value)}
                  placeholder='Meta Description'
                  rows={1}
                />
              </div>
            </div>
            <div className='mb-4'>
              <label className='form-label'>Meta Keywords</label>
              <div className='d-flex mb-2'>
                <input
                  type='text'
                  className='form-control me-2'
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddKeyword()
                    }
                  }}
                  placeholder='Add keywords separated by commas and press Enter'
                />
                <button type='button' className='btn btn-primary' onClick={handleAddKeyword}>
                  Add
                </button>
              </div>
              <div className='d-flex flex-wrap gap-2'>
                {(currentTranslation.metaKeywords || []).map((kw, idx) => (
                  <span key={idx} className='badge badge-light-primary'>
                    {kw}
                    <button
                      type='button'
                      className='btn btn-sm btn-link text-danger ms-1 p-0'
                      onClick={() => handleRemoveKeyword(idx)}
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className='mb-8'>
            <h3 className='mb-4'>Open Graph</h3>
            <div className='row mb-4'>
              <div className='col-md-6'>
                <label className='form-label'>OG Title</label>
                <input
                  type='text'
                  className='form-control'
                  value={currentTranslation.openGraphTitle || ''}
                  onChange={(e) => updateTranslation('openGraphTitle', e.target.value)}
                  placeholder='Open Graph Title'
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>OG Description</label>
                <textarea
                  className='form-control'
                  value={currentTranslation.openGraphDescription || ''}
                  onChange={(e) => updateTranslation('openGraphDescription', e.target.value)}
                  placeholder='Open Graph Description'
                  rows={1}
                />
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-md-6'>
                <label className='form-label'>OG Image URL</label>
                <input
                  type='text'
                  className='form-control'
                  value={currentTranslation.openGraphImage || ''}
                  onChange={(e) => updateTranslation('openGraphImage', e.target.value)}
                  placeholder='Open Graph Image URL'
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>OG Type</label>
                <select
                  className='form-select'
                  value={currentTranslation.openGraphType || 'website'}
                  onChange={(e) => updateTranslation('openGraphType', e.target.value)}
                >
                  <option value='website'>Website</option>
                  <option value='article'>Article</option>
                  <option value='product'>Product</option>
                </select>
              </div>
            </div>
          </div>

          <div className='mb-8'>
            <h3 className='mb-4'>Twitter Card</h3>
            <div className='row mb-4'>
              <div className='col-md-6'>
                <label className='form-label'>Twitter Title</label>
                <input
                  type='text'
                  className='form-control'
                  value={currentTranslation.twitterTitle || ''}
                  onChange={(e) => updateTranslation('twitterTitle', e.target.value)}
                  placeholder='Twitter Title'
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Twitter Description</label>
                <textarea
                  className='form-control'
                  value={currentTranslation.twitterDescription || ''}
                  onChange={(e) => updateTranslation('twitterDescription', e.target.value)}
                  placeholder='Twitter Description'
                  rows={1}
                />
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-md-6'>
                <label className='form-label'>Twitter Image URL</label>
                <input
                  type='text'
                  className='form-control'
                  value={currentTranslation.twitterImage || ''}
                  onChange={(e) => updateTranslation('twitterImage', e.target.value)}
                  placeholder='Twitter Image URL'
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Twitter Card Type</label>
                <select
                  className='form-select'
                  value={currentTranslation.twitterCardType || 'summary_large_image'}
                  onChange={(e) => updateTranslation('twitterCardType', e.target.value)}
                >
                  <option value='summary'>Summary</option>
                  <option value='summary_large_image'>Summary Large Image</option>
                </select>
              </div>
            </div>
          </div>

          <div className='mb-8'>
            <h3 className='mb-4'>Additional Settings</h3>
            <div className='row mb-4'>
              <div className='col-md-6'>
                <label className='form-label'>Meta Robots</label>
                <input
                  type='text'
                  className='form-control'
                  value={currentTranslation.metaRobots || 'index,follow'}
                  onChange={(e) => updateTranslation('metaRobots', e.target.value)}
                  placeholder='index,follow'
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Canonical URL</label>
                <input
                  type='text'
                  className='form-control'
                  value={currentTranslation.canonicalUrl || ''}
                  onChange={(e) => updateTranslation('canonicalUrl', e.target.value)}
                  placeholder='Canonical URL'
                />
              </div>
            </div>
            <div className='mb-4'>
              <label className='form-label'>Structured Data (JSON-LD)</label>
              <textarea
                className='form-control'
                value={currentTranslation.structuredData || ''}
                onChange={(e) => updateTranslation('structuredData', e.target.value)}
                placeholder='Structured Data in JSON-LD format'
                rows={5}
              />
            </div>
          </div>

          <div className='d-flex justify-content-end'>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleSave}
              disabled={isSaving || formik.isSubmitting}
            >
              {isSaving || formik.isSubmitting ? (
                <>
                  <span
                    className='spinner-border spinner-border-sm'
                    role='status'
                    aria-hidden='true'
                  ></span>
                  <span className='ms-2'>{intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}</span>
                </>
              ) : (
                intl.formatMessage({id: 'TOUR.ACTIONS.SAVE'})
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export {SeoTab}
